import { useEffect, useState } from 'react'
import { Card } from './ui'
import { disable, enable, getState, sendTest, type PushState } from '../lib/push'

/**
 * Til/fra for beskeder på telefonen (beslutning 032).
 *
 * Kortet ligger på notifikationssiden, fordi det er der, folk opdager, at de
 * ikke fik besked i tide.
 */
export function PushCard() {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void getState().then(setState)
  }, [])

  if (state === null || state === 'unsupported') {
    // Ingen grund til at fortælle en gammel browser, hvad den ikke kan.
    return null
  }

  async function run(action: () => Promise<void>, done: string) {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await action()
      setState(await getState())
      setMessage(done)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Noget gik galt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Beskeder på telefonen">
      {state === 'ios-needs-install' && (
        <div className="small muted">
          På iPhone og iPad virker beskeder kun, når appen ligger på hjemmeskærmen. Tryk på
          del-ikonet i Safari og vælg <strong>Føj til hjemmeskærm</strong> - åbn den derfra, så
          kan du slå beskeder til.
        </div>
      )}

      {state === 'denied' && (
        <div className="small muted">
          Du har sagt nej til beskeder fra siden. Det kan kun laves om i browserens egne
          indstillinger for flow.tc-el.dk - derefter kan du slå dem til her.
        </div>
      )}

      {state === 'off' && (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Få besked på telefonen, når du får en opgave, bliver nævnt i en kommentar, eller en
            deadline nærmer sig. Uden det skal du selv åbne appen for at se dem.
          </p>
          <button
            className="btn btn--primary"
            disabled={busy}
            onClick={() => run(enable, 'Beskeder er slået til på denne enhed.')}
          >
            {busy ? 'Slår til ...' : 'Slå beskeder til'}
          </button>
        </>
      )}

      {state === 'on' && (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Beskeder er slået til på denne enhed. Har du flere telefoner eller computere, skal de
            slås til hver for sig.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const result = await sendTest()
                  if (result.delivered === 0) {
                    throw new Error(
                      'Beskeden kunne ikke leveres. Prøv at slå fra og til igen på denne enhed.'
                    )
                  }
                }, 'Prøvebeskeden er sendt - den dukker op om et øjeblik.')
              }
            >
              Send en prøvebesked
            </button>
            <button
              className="btn"
              disabled={busy}
              onClick={() => run(disable, 'Beskeder er slået fra på denne enhed.')}
            >
              Slå fra
            </button>
          </div>
        </>
      )}

      {message && (
        <div className="small" style={{ marginTop: 10, color: 'var(--success)' }}>
          {message}
        </div>
      )}
      {error && (
        <div className="small" style={{ marginTop: 10, color: 'var(--danger)' }}>
          {error}
        </div>
      )}
    </Card>
  )
}
